import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_PAYMENT_TERMS,
  POSTAL_ADDRESS_COLUMNS,
  SOMASI_LETTERHEAD,
  applicableInvoiceNoteIds,
  richTextToPlain,
} from "@somasi/shared";
import type { InvoiceNote, LoggedBundleItem, ServiceBundleItemWithService } from "@somasi/shared";
import { FSC_CHARGE_DESCRIPTION, loggedServiceLines, serviceEventBundleKind } from "@somasi/shared";
import { resolveInvoiceBillTo } from "@somasi/shared/invoice-pdf";
import { db } from "@/integrations/aws/data";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/rich-text-editor";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Boxes, Check, ChevronsUpDown, Plus, Search, Trash2, Wand2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { money, resolveCompanyPriceList } from "@/lib/billing";
import { RATE_SOURCES, RATE_SOURCE_LABEL, feeForRateSource, type RateSource } from "@/lib/pricing";
import { takeBundleItems, type PickedBundle } from "@/components/bundle-picker";
import { Truncated } from "@/components/truncated";
import { createInvoice, requestInvoiceArchive } from "@/integrations/aws/api";
import {
  CompanySelect,
  PICKER_LIMIT,
  searchServiceBundles,
  type CompanyRow,
} from "@/components/entity-select";
import { useDebounced } from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/invoices/new")({
  head: () => ({
    meta: [
      { title: "New Invoice — Somasi Ops" },
      {
        name: "description",
        content:
          "Create a client invoice with fees, unbilled work and recoverable expenses pre-filled.",
      },
      { property: "og:title", content: "New Invoice — Somasi Ops" },
      {
        property: "og:description",
        content:
          "Create a client invoice with fees, unbilled work and recoverable expenses pre-filled.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: NewInvoicePage,
});

type Line = {
  key: string;
  description: string;
  quantity: number;
  unit_amount: number;
  service_id: string | null;
  /**
   * The charge's name as it will read on the invoice.
   *
   * For a catalogue service this is the service's name, kept so the picker has
   * something to show without looking the id up. For a charge named on the line
   * alone it is the only name there is.
   */
  label: string;
  /**
   * Where the line came from, and so what it may be edited into.
   *
   * `service` is a catalogue service, which offers the two published rates.
   * `manual` is a charge named on this line alone, at a figure typed in. The
   * other three were drawn from the work log and carry the amount recorded
   * there — a line added blank can be either of the first two, and nothing can
   * become one of the last three.
   */
  source_type: "service" | "service_event" | "sundry" | "virrgin" | "manual";
  source_id: string | null;
  /**
   * Where this line's rate comes from, and so whether the figure is typed in or
   * re-read when the invoice date moves. A charge named on the line alone can
   * only be `custom`; work drawn from the log keeps the amount it was logged at,
   * which is also `custom` — nothing published has an opinion about it.
   */
  rate_source: RateSource;
  /**
   * When the work on this line was delivered, as YYYY-MM-DD.
   *
   * Asked for, and editable, on a line this invoice composed: a service billed
   * straight onto an invoice is work nothing has recorded yet, and the date is
   * what gets it into the work log — billed — as the invoice is saved. Without
   * it the register would know the client had been charged for an audit and the
   * work log would not know the audit had happened.
   *
   * A line drawn from the log carries the date it is already recorded under,
   * shown but not editable: that row is the record, and the place to correct it
   * is the work log.
   */
  service_date: string;
};

/** The three kinds of logged work an invoice can draw on. */
type PickKind = "service_event" | "sundry" | "virrgin";

/** The columns of a logged service that turning it into lines turns on. */
type LoggedServiceEvent = {
  id: string;
  /** When it was delivered — the date the lines it becomes are logged under. */
  event_date: string;
  description: string | null;
  quantity: number | null;
  amount: number | null;
  service_id: string | null;
  bundle_id: string | null;
  bundle_name: string | null;
  bundle_items: LoggedBundleItem[] | null;
};

/**
 * One piece of unbilled work as the pickers offer it: what to show, and the
 * invoice line it becomes if it is taken.
 *
 * Carrying the finished line with the row is what lets both pickers work in
 * keys alone — neither "add selected" nor the charge picker has to know which
 * of the three kinds of work it is looking at.
 */
type Pick = {
  key: string;
  kind: PickKind;
  date: string;
  /** What the work was, as logged. */
  title: string;
  /** The secondary word a kind may carry — an expense's type, or a bundle. */
  note: string | null;
  /** What the whole record bills, however many lines it becomes. */
  amount: number;
  /**
   * The lines this record becomes. Usually one.
   *
   * A bundle logged as a single entry is the exception: it is one row in the work
   * log and one charge agreed with the client, but the client's invoice itemises
   * the services it covered — so it arrives here as several lines, all naming the
   * same logged row as their source.
   */
  lines: Omit<Line, "key">[];
};

const PICK_KIND_LABEL: Record<PickKind, string> = {
  service_event: "Service",
  sundry: "Sundry",
  virrgin: "Virrgin / FSC",
};

/** How many rows the unbilled list draws before asking to be shown more. */
const PICK_PAGE = 25;

/** Identifies a record across the pickers and the lines already drafted. */
const pickKey = (source_type: Line["source_type"], id: string) => `${source_type}:${id}`;

/** Everything a row can be searched on, flattened once. */
const pickHaystack = (p: Pick) =>
  `${p.date} ${p.title} ${p.note ?? ""} ${PICK_KIND_LABEL[p.kind]}`.toLowerCase();

/**
 * A service as a line needs it: its name, and the rates on offer for it.
 *
 * The catalogue and the price list answer different halves of this. Every active
 * service comes from the catalogue, which needs no party; the two published rates
 * come from the price list, which does. A service the price list has not answered
 * for yet still appears, at its catalogue fee and with no group rate — so the
 * picker is never short, and a rate never appears from nowhere.
 */
type Chargeable = {
  service_id: string;
  code: string;
  name: string;
  description: string;
  in_plan: boolean;
  standard_fee: number;
  standard_effective_from: string | null;
  group_fee: number | null;
  group_effective_from: string | null;
};

const uid = () => Math.random().toString(36).slice(2);

/** A date box left empty reads as "", which is not a date the work log takes. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True for the two kinds of line an operator composes rather than draws in. */
const isComposed = (line: Line) => line.source_type === "service" || line.source_type === "manual";

/**
 * A line nothing has been put on yet — what "Add line" leaves behind.
 *
 * Asked before a bundle consumes the line its picker was opened on, so the test
 * is deliberately strict: anything named, described or priced counts as typed in,
 * and is kept.
 */
const isBlank = (line: Line) =>
  !line.label.trim() && !line.description.trim() && line.unit_amount === 0 && !line.source_id;

function NewInvoicePage() {
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  /**
   * The company being billed, held whole rather than looked up.
   *
   * The register used to be read whole here so that a dropdown could filter it
   * and `find()` could then recover the chosen row. The picker searches the API
   * now, and hands back the row it chose — which is the only row this page ever
   * needed.
   */
  const [company, setCompany] = useState<CompanyRow | null>(null);
  const companyId = company?.id ?? "";
  /**
   * The client group the invoice is written against.
   *
   * Never asked for: it is read off the company below, and stays empty for a
   * company attached to no group.
   */
  const [groupId, setGroupId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today);
  /**
   * Due on receipt — the terms every invoice carries — means due the day it is
   * raised, so the due date tracks the invoice date rather than sitting a
   * month behind it and contradicting the terms the document prints. Still
   * editable for the invoice that settles on its own schedule.
   */
  const [dueDate, setDueDate] = useState(today);
  const [period, setPeriod] = useState("");
  const [bankId, setBankId] = useState("");
  const [contactId, setContactId] = useState("");
  const [noteIds, setNoteIds] = useState<string[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  /** The unbilled records ticked in the picker but not yet taken onto the invoice. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pickSearch, setPickSearch] = useState("");
  const [pickKind, setPickKind] = useState<"all" | PickKind>("all");
  const [pickLimit, setPickLimit] = useState(PICK_PAGE);
  const [freeNotes, setFreeNotes] = useState("");
  /**
   * What the invoice will print where it differs from the records behind it.
   *
   * `null` means nothing has been touched, and every box shows the default it
   * would print anyway. Picking a different company clears it, so the boxes
   * cannot keep showing the last company's address.
   */
  const [printing, setPrinting] = useState<null | {
    terms: string;
    bill_to_name: string;
    bill_to_for: string;
    bill_to_address: string;
    from_name: string;
    from_address: string;
  }>(null);
  const [saving, setSaving] = useState(false);

  const { data: groups = [] } = useQuery({
    queryKey: ["client_groups_invoice"],
    queryFn: async () =>
      (
        await api.clientGroups
          .list()
          .all()
          // The address seeds what the invoice prints, so it is read with the
          // list rather than fetched again once a group is picked.
          .select(`id, name, fee_schedule_id, country, ${POSTAL_ADDRESS_COLUMNS.join(", ")}`)
          .order("name")
      ).data ?? [],
  });
  const { data: banks = [] } = useQuery({
    queryKey: ["bank_accounts_active"],
    queryFn: async () =>
      (await api.bankAccounts.list().all().eq("active", true).order("account_name")).data ?? [],
  });
  const { data: notes = [] } = useQuery({
    queryKey: ["invoice_notes_active"],
    queryFn: async () =>
      (await api.invoiceNotes.list().all().eq("active", true).order("sort_order").order("label"))
        .data ?? [],
  });
  /**
   * The whole service catalogue, read on its own rather than taken from the
   * price list.
   *
   * The price list only answers once a party is chosen, and it is the rates that
   * need a party — the catalogue does not. Reading it separately means the
   * picker offers every service it always offers, and the rates attach to them
   * as they resolve.
   */
  const { data: catalogue = [] } = useQuery({
    queryKey: ["services_chargeable"],
    queryFn: async () =>
      (
        await api.services
          .list()
          .all()
          .select("id, code, name, invoice_description, internal_description, default_fee")
          .eq("active", true)
          .order("name")
      ).data ?? [],
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts_lite"],
    queryFn: async () =>
      (
        await api.contacts
          .list()
          .all()
          .select("id, full_name, primary_email, company_id, client_group_id")
          .order("full_name")
      ).data ?? [],
  });

  useEffect(() => {
    const def = banks.find((b) => b.is_default) ?? banks[0];
    if (def && !bankId) setBankId(def.id);
  }, [banks]);

  /**
   * The notes this invoice starts with, by the rule in `@somasi/shared`: the
   * standard wording, plus whatever is attached to this company, its client
   * group, or the account the invoice is payable into.
   *
   * It used to be every active note, which was right while every note was
   * Somasi's own boilerplate and wrong the moment one of them was about a single
   * client or a single account — that note then printed on everything until
   * somebody noticed and unticked it. The same rule resolves a draft that names
   * no notes server-side, so an imported invoice and a hand-made one agree.
   */
  const noteTarget = useMemo(
    () => ({
      companyId: companyId || null,
      clientGroupId: groupId || null,
      bankAccountId: bankId || null,
    }),
    [companyId, groupId, bankId],
  );

  const autoNoteIds = useMemo(
    () => applicableInvoiceNoteIds(notes, noteTarget),
    [notes, noteTarget],
  );

  /** Notes added to this invoice by hand, over and above what the rule brings. */
  const [extraNoteIds, setExtraNoteIds] = useState<string[]>([]);

  /**
   * Notes the operator has deliberately taken off this invoice.
   *
   * Changing the company or the account re-runs the rule — that is the point of
   * attaching wording to them — and it must not undo an untick along the way.
   * Kept rather than inferred from the current ticks, because a note that stops
   * applying and starts again should come back only if nobody refused it.
   */
  const untickedNotes = useRef(new Set<string>());

  /**
   * The notes on this invoice: what the rule brings, plus what was added.
   *
   * The whole active list used to be shown with everything ticked, so an invoice
   * could carry another client's clause by nobody's decision. What arrives by
   * itself is now only the two things — standard wording, or wording attached to
   * this company, its group or this account — and anything else is somebody
   * choosing it from the picker below.
   *
   * Taken in `notes` order rather than in the order things were added, because
   * that is the order they print in and the order the notes screen exists to set.
   */
  const shownNotes = useMemo(() => {
    const applicable = new Set(autoNoteIds);
    const added = new Set(extraNoteIds);
    return notes.filter((note) => applicable.has(note.id) || added.has(note.id));
  }, [notes, autoNoteIds, extraNoteIds]);

  /** The rest of the active list, for the picker — what is here is not offered. */
  const addableNotes = useMemo(() => {
    const shown = new Set(shownNotes.map((note) => note.id));
    return notes.filter((note) => !shown.has(note.id));
  }, [notes, shownNotes]);

  const ticked = (ids: { id: string }[]) =>
    ids.map((note) => note.id).filter((id) => !untickedNotes.current.has(id));

  useEffect(() => {
    setNoteIds(ticked(shownNotes));
  }, [shownNotes]);

  // An invoice is always raised to a company, and a company belongs to at most
  // one client group — so the group is read off the company rather than asked
  // for. Naming a different one would contradict the register, so it is shown
  // and not editable, and a company in no group leaves it null.
  useEffect(() => {
    setGroupId(company?.client_group_id ?? "");
    setPrinting(null);
  }, [company?.client_group_id]);

  // The picker carries only what it needs to search; the registered office is
  // read for the one company that was actually chosen.
  const { data: billedCompany } = useQuery({
    queryKey: ["invoice_new_company", companyId],
    enabled: !!companyId,
    queryFn: async () =>
      (
        await api.companies.get(companyId, [
          "id",
          "legal_name",
          "registered_office",
          "client_group_id",
        ])
      ).data,
  });

  const group = groups.find((g) => g.id === groupId) ?? null;
  const groupHasSchedule = !!group?.fee_schedule_id;

  /**
   * What the invoice prints if nothing here is touched: the addressee and its
   * address resolved exactly as the renderer resolves them — the client group
   * when the company is in one, the company's registered office when it is
   * not — and the Somasi letterhead.
   */
  const printedDefaults = resolveInvoiceBillTo({
    company: billedCompany,
    group,
    contactEmail: null,
  });
  const defaultPrinting = {
    terms: printedDefaults.terms ?? DEFAULT_PAYMENT_TERMS,
    bill_to_name: companyId ? printedDefaults.billTo.name : "",
    bill_to_for: companyId ? (printedDefaults.billTo.onBehalfOf ?? "") : "",
    bill_to_address: companyId ? (printedDefaults.billTo.addressLines ?? []).join("\n") : "",
    from_name: SOMASI_LETTERHEAD.name,
    from_address: SOMASI_LETTERHEAD.addressLines.join("\n"),
  };
  const printingForm = printing ?? defaultPrinting;
  /** Only the difference is stored; a box still at its default saves as null. */
  const printedOverride = (field: keyof typeof defaultPrinting) => {
    const value = printingForm[field].trim();
    return !value || value === defaultPrinting[field].trim() ? null : value;
  };

  /**
   * Where a newly chosen service takes its rate from before anyone says otherwise.
   *
   * The group's schedule is the rate a client has actually negotiated, so it is
   * the sensible opening answer wherever there is one; without it the only level
   * left is the catalogue.
   */
  const defaultSource: RateSource = groupHasSchedule ? "client_group" : "somasi_standard";

  // Priced as at the invoice date, not as at today. Each schedule holds a
  // version per period, so an invoice back-dated into a previous period has to
  // resolve against that period's rates — which is why the date is part of the
  // cache key, and why moving it reprices the lines below.
  //
  // The company resolves the whole cascade: its own fee or schedule, then its
  // client group's, then the catalogue. The answer carries the catalogue fee and
  // the group's fee alongside the resolved one, which is what lets a line be
  // raised at either.
  const { data: priceList = [] } = useQuery({
    queryKey: ["price_list", companyId, invoiceDate],
    enabled: !!companyId,
    queryFn: () => resolveCompanyPriceList(companyId, invoiceDate),
  });

  const { data: events = [] } = useQuery({
    queryKey: ["unbilled_events", companyId],
    enabled: !!companyId,
    queryFn: async () =>
      (
        await api.workLog.events
          .list()
          .all()
          .eq("company_id", companyId)
          .is("invoice_id", null)
          .order("event_date")
      ).data ?? [],
  });
  const { data: sundry = [] } = useQuery({
    queryKey: ["unbilled_sundry", companyId],
    enabled: !!companyId,
    queryFn: async () =>
      (
        await api.workLog.expenses
          .list()
          .all()
          .eq("company_id", companyId)
          .is("invoice_id", null)
          .order("expense_date")
      ).data ?? [],
  });
  const { data: virrgin = [] } = useQuery({
    queryKey: ["unbilled_virrgin", companyId],
    enabled: !!companyId,
    // Deposits top the float up and belong to no company — only charges are
    // billable. The registry fees this company owes, in other words: the
    // statement import matched each of them to it by the IBC number the FSC
    // printed, and this is where that matching is cashed in.
    queryFn: async () =>
      (
        await api.virrginTransactions
          .list()
          .all()
          .eq("company_id", companyId)
          .eq("txn_type", "charge")
          .is("invoice_id", null)
          .order("txn_date")
      ).data ?? [],
  });

  // Switching the company changes which work is billable and which rates apply,
  // so the lines already drafted no longer belong to the invoice being written.
  useEffect(() => {
    setLines([]);
    setPicked(new Set());
    setPickSearch("");
    setPickKind("all");
    setContactId("");
  }, [companyId]);

  const has = (source_type: Line["source_type"], id: string) =>
    lines.some((l) => l.source_type === source_type && l.source_id === id);
  /**
   * The table of lines, held only to scroll it.
   *
   * Everything added goes on top, and the box of lines has a ceiling it scrolls
   * under — so a line added while scrolled down would land out of sight and the
   * button would look like it had done nothing. `parentElement` is the scroll
   * box `Table` draws around itself.
   */
  const linesTable = useRef<HTMLTableElement>(null);
  const revealLines = () =>
    linesTable.current?.parentElement?.scrollTo({ top: 0, behavior: "smooth" });

  /**
   * Puts work on the invoice, newest first.
   *
   * On top rather than on the end: the line just added is the one being worked
   * on, and it is the end of a long invoice that is off screen.
   */
  const addLines = (ls: Omit<Line, "key">[], consumeKey?: string) => {
    setLines((prev) => [
      ...ls.map((l) => ({ ...l, key: uid() })),
      // `consumeKey` is the line the new ones were raised *from* — the blank line
      // whose charge picker was used to choose a bundle. Dropping it is what makes
      // picking a bundle there read as filling that line in, rather than as
      // leaving an empty row above the lines it produced.
      //
      // Still-blank is asked here, against `prev`, rather than by the caller: a
      // bundle is fetched before it can be expanded, and a line typed into while
      // that request was in flight must not be thrown away by a decision taken
      // before it was.
      ...prev.filter((l) => l.key !== consumeKey || !isBlank(l)),
    ]);
    revealLines();
  };
  const addLine = (l: Omit<Line, "key">) => addLines([l]);
  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const patchLine = (key: string, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const eventPicks = useMemo<Pick[]>(
    () =>
      events.map((e) => ({
        key: pickKey("service_event", e.id),
        kind: "service_event" as const,
        date: e.event_date,
        title: e.description ?? "Service delivered",
        // What the work log recorded about the bundle, said here too — including
        // that a bundled entry is about to become several lines.
        note: bundleNote(e),
        amount: Number(e.amount ?? 0) * Number(e.quantity ?? 1),
        lines: eventLines(e, period),
      })),
    [events, period],
  );
  const sundryPicks = useMemo<Pick[]>(
    () =>
      sundry.map((e) => ({
        key: pickKey("sundry", e.id),
        kind: "sundry" as const,
        date: e.expense_date,
        title: e.description,
        note: e.expense_type,
        amount: Number(e.amount) * (1 + Number(e.markup_pct ?? 0) / 100),
        lines: [
          {
            description: expenseDescription(e),
            quantity: 1,
            unit_amount: Number(e.amount) * (1 + Number(e.markup_pct ?? 0) / 100),
            service_id: null,
            label: e.description,
            source_type: "sundry",
            source_id: e.id,
            rate_source: "custom",
            service_date: e.expense_date,
          },
        ],
      })),
    [sundry],
  );
  const virrginPicks = useMemo<Pick[]>(
    () =>
      virrgin.map((t) => ({
        key: pickKey("virrgin", t.id),
        kind: "virrgin" as const,
        date: t.txn_date,
        title: t.description?.trim() || FSC_CHARGE_DESCRIPTION,
        // The registry number the statement named the company by, so a fee can
        // be checked back against the statement line it came from.
        note: t.registration_number ? `Reg. no. ${t.registration_number}` : null,
        amount: Number(t.amount),
        lines: [
          {
            description: virrginDescription(t),
            quantity: 1,
            unit_amount: Number(t.amount),
            service_id: null,
            label: t.description?.trim() || FSC_CHARGE_DESCRIPTION,
            source_type: "virrgin",
            source_id: t.id,
            rate_source: "custom",
            service_date: t.txn_date,
          },
        ],
      })),
    [virrgin],
  );
  const allPicks = useMemo(
    () => [...eventPicks, ...sundryPicks, ...virrginPicks],
    [eventPicks, sundryPicks, virrginPicks],
  );

  /**
   * What is already on the invoice, so the picker can show it ticked and fixed
   * rather than offering it a second time. A line removed below puts its record
   * back on offer.
   */
  const taken = useMemo(
    () =>
      new Set(
        lines
          .filter((l) => l.source_id !== null)
          .map((l) => pickKey(l.source_type, l.source_id as string)),
      ),
    [lines],
  );

  /** What is still on offer: logged, unbilled, and not already on a line. */
  const offered = useMemo(() => allPicks.filter((p) => !taken.has(p.key)), [allPicks, taken]);

  const kindCounts = useMemo(() => {
    const counts: Record<PickKind, number> = { service_event: 0, sundry: 0, virrgin: 0 };
    for (const p of offered) counts[p.kind] += 1;
    return counts;
  }, [offered]);

  /**
   * The rows the list is showing: the chosen kind, narrowed by what was typed.
   *
   * A company with a hundred items logged is the case this has to survive, so
   * the list filters and pages rather than laying every row out at once.
   */
  const filtered = useMemo(() => {
    const needle = pickSearch.trim().toLowerCase();
    return offered.filter(
      (p) =>
        (pickKind === "all" || p.kind === pickKind) &&
        (!needle || pickHaystack(p).includes(needle)),
    );
  }, [offered, pickKind, pickSearch]);

  const shown = filtered.slice(0, pickLimit);

  // Narrowing the list starts the paging again, so "show more" never carries a
  // count from a search that is no longer on screen.
  useEffect(() => {
    setPickLimit(PICK_PAGE);
  }, [pickSearch, pickKind]);

  const togglePick = (key: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Ticks or clears everything the current search and filter match. */
  const toggleFiltered = (on: boolean) =>
    setPicked((s) => {
      const next = new Set(s);
      for (const p of filtered) {
        if (on) next.add(p.key);
        else next.delete(p.key);
      }
      return next;
    });

  /**
   * What is ticked, across the whole list rather than the current view — a
   * search narrowed after ticking must not quietly drop what was chosen.
   */
  const selection = useMemo(() => offered.filter((p) => picked.has(p.key)), [offered, picked]);
  const selectionTotal = selection.reduce((sum, p) => sum + p.amount, 0);
  const filteredPickedCount = filtered.filter((p) => picked.has(p.key)).length;

  const addPicks = (picks: Pick[]) => {
    if (picks.length === 0) return;
    const added = picks.flatMap((p) => p.lines);
    addLines(added);
    setPicked(new Set());

    // Items and lines are counted separately because a bundled entry is one item
    // and several lines, and "added 1 item" over five new rows would read as a
    // mistake.
    const items = `${picks.length} item${picks.length === 1 ? "" : "s"}`;
    notify.success(
      added.length === picks.length
        ? `Added ${items} to the invoice`
        : `Added ${items} to the invoice as ${added.length} lines`,
    );
  };

  /**
   * Points an existing line at a piece of logged work, in place.
   *
   * In place, and not with `addLines`, so the line keeps its position: the
   * operator chose this row of the invoice. A bundled entry becomes several
   * lines, which take that one line's place rather than landing at the top.
   */
  const usePick = (key: string, p: Pick) =>
    setLines((ls) => {
      const at = ls.findIndex((l) => l.key === key);
      if (at < 0) return ls;
      const made = p.lines.map((l) => ({ ...l, key: uid() }));
      return [...ls.slice(0, at), ...made, ...ls.slice(at + 1)];
    });

  /**
   * What a line's charge picker may offer: everything still free, plus whatever
   * that line is drawn from now — which is taken, and would otherwise be the one
   * row missing from the list it is meant to be ticked in.
   */
  const picksForLine = (l: Line) => {
    if (isComposed(l) || !l.source_id) return offered;
    const own = allPicks.find((p) => p.key === pickKey(l.source_type, l.source_id as string));
    return own ? [own, ...offered] : offered;
  };

  /**
   * The catalogue with its rates attached, as the picker offers it.
   *
   * What this client actually subscribes to floats to the top: the list runs to
   * the whole catalogue, not just the services a schedule happened to price, so
   * the handful on the plan would otherwise be lost among the rest.
   */
  const chargeables = useMemo<Chargeable[]>(() => {
    const rates = new Map(priceList.map((p) => [p.service_id, p]));

    return catalogue
      .map((s) => {
        const priced = rates.get(s.id);
        if (priced) {
          return {
            service_id: priced.service_id,
            code: priced.code,
            name: priced.name,
            description: priced.description,
            in_plan: priced.in_plan,
            standard_fee: priced.standard_fee,
            standard_effective_from: priced.standard_effective_from,
            group_fee: priced.group_fee,
            group_effective_from: priced.group_effective_from,
          };
        }
        // No party chosen yet, or the list has not arrived. The catalogue's own
        // fee is the only rate anyone has published for this service.
        return {
          service_id: s.id,
          code: s.code,
          name: s.name,
          description: s.invoice_description || s.internal_description || s.name,
          in_plan: false,
          standard_fee: Number(s.default_fee ?? 0),
          standard_effective_from: null,
          group_fee: null,
          group_effective_from: null,
        };
      })
      .sort((a, b) => Number(b.in_plan) - Number(a.in_plan) || a.name.localeCompare(b.name));
  }, [catalogue, priceList]);

  const priced = (serviceId: string | null) =>
    chargeables.find((s) => s.service_id === serviceId) ?? null;

  /** The description a service writes into a line it is chosen on. */
  const autoDescription = (s: Chargeable) =>
    period ? `${s.description} — ${period}` : s.description;

  /**
   * Re-reads the rate of every untouched service line when the price list moves.
   *
   * Moving the invoice date is what moves it: a different date resolves a
   * different version of the schedule, and a line added before the date was
   * corrected would otherwise go out at a rate nobody agreed for that period. A
   * line whose rate has been typed over is left alone — that override is the
   * operator's answer and outranks the schedule's.
   */
  useEffect(() => {
    if (priceList.length === 0) return;
    setLines((ls) =>
      ls.map((l) => {
        if (l.source_type !== "service" || l.rate_source === "custom") return l;
        const match = chargeables.find((s) => s.service_id === l.service_id);
        if (!match) return l;
        const rate = feeForRateSource(match, l.rate_source);
        return rate == null || rate === l.unit_amount ? l : { ...l, unit_amount: rate };
      }),
    );
  }, [chargeables, priceList.length]);

  /**
   * Moves the delivery date of every composed line that was still on the old
   * invoice date.
   *
   * A line is added before anybody notices the invoice is dated today rather
   * than the end of the period it bills, and that line's date — which opened at
   * the invoice date — would otherwise stay behind and log the work in the wrong
   * month. A date typed over is the operator's answer about when the work
   * happened and is left exactly where it is.
   */
  const lastInvoiceDate = useRef(invoiceDate);
  useEffect(() => {
    const was = lastInvoiceDate.current;
    lastInvoiceDate.current = invoiceDate;
    if (was === invoiceDate) return;
    setLines((ls) =>
      ls.map((l) =>
        isComposed(l) && l.service_date === was ? { ...l, service_date: invoiceDate } : l,
      ),
    );
  }, [invoiceDate]);

  /**
   * Names a catalogue service on a line, filling in its description and rate.
   *
   * The rate comes from the level this client is normally priced at, unless that
   * level does not price the service — a group schedule covering three services
   * of twenty does not leave the other seventeen unpriced, it leaves them at the
   * published rate, so the line opens on the source that actually has an answer.
   */
  const chooseService = (key: string, serviceId: string) => {
    const line = lines.find((l) => l.key === key);
    const s = priced(serviceId);
    if (!line || !s) return;

    const source = feeForRateSource(s, defaultSource) != null ? defaultSource : "somasi_standard";
    const rate = feeForRateSource(s, source) ?? 0;

    // Only overwrite a description nobody has written: either still empty, or
    // still exactly what the service previously named on this line put there.
    const previous = priced(line.service_id);
    const untouched =
      !line.description.trim() || (!!previous && line.description === autoDescription(previous));

    patchLine(key, {
      source_type: "service",
      service_id: serviceId,
      source_id: serviceId,
      label: s.name,
      rate_source: source,
      unit_amount: rate,
      description: untouched ? autoDescription(s) : line.description,
    });
  };

  /**
   * Names a charge that is not in the catalogue, on this line alone.
   *
   * Nothing is written to the service catalogue: this is the charge that comes up
   * once, and adding a row for it would leave the catalogue full of one-offs that
   * every fee schedule then has to decide whether to price. With no catalogue
   * entry there is no published rate either, so the figure is typed in.
   */
  const nameOnLine = (key: string, label: string) => {
    const line = lines.find((l) => l.key === key);
    const text = label.trim();
    if (!line || !text) return;

    const previous = priced(line.service_id);
    const untouched =
      !line.description.trim() || (!!previous && line.description === autoDescription(previous));

    patchLine(key, {
      source_type: "manual",
      service_id: null,
      source_id: null,
      label: text,
      rate_source: "custom",
      description: untouched ? text : line.description,
    });
  };

  /** Moves one line to another rate source, re-filling its figure. */
  const setRateSource = (key: string, source: RateSource) => {
    if (source === "custom") return patchLine(key, { rate_source: "custom" });

    const line = lines.find((l) => l.key === key);
    const match = priced(line?.service_id ?? null);
    const rate = match ? feeForRateSource(match, source) : null;
    if (rate == null) {
      return notify.error("The client group's fee schedule does not price this service");
    }
    patchLine(key, { rate_source: source, unit_amount: rate });
  };

  const addBlankLine = () =>
    addLine({
      description: "",
      quantity: 1,
      unit_amount: 0,
      service_id: null,
      label: "",
      source_type: "manual",
      source_id: null,
      rate_source: "custom",
      // The invoice's own date is the sensible opening answer — most invoices
      // bill work finished the day they go out — and it is editable per line.
      service_date: invoiceDate,
    });

  /**
   * Adds a line per service in a bundle, priced the way this client is priced.
   *
   * Reached from a line's own charge picker, where the bundles sit above the
   * services: choosing one there is choosing what the line is for, and a bundle's
   * answer to that happens to be several lines rather than one. `fromKey` is the
   * line it was chosen on, which is dropped if nothing had been typed on it.
   *
   * The bundle contributes two things and no more: which services, and how many
   * of each. Each line then goes through exactly the rate resolution a service
   * picked by hand would — the client's own level if it prices the service, the
   * published rate if it does not — so a bundle can never put a figure on an
   * invoice that disagrees with the schedule the client is on.
   *
   * `source_type` is `service`, not a kind of its own, which is what keeps the
   * lines re-reading their rate when the invoice date moves and leaves nothing on
   * the saved invoice that remembers a bundle was involved.
   *
   * This is the catalogue's bundle, priced here for the first time. A bundle that
   * was already *delivered* comes the other way — it is logged in the work log and
   * arrives in the unbilled list above, where it is itemised at the figures it was
   * logged at rather than repriced. See `eventLines`.
   *
   * A service the resolver has no rate for lands at zero on `custom` and says so,
   * rather than being left out: the operator asked for the bundle, so the line
   * belongs on the invoice with a figure to fill in.
   */
  const expandBundle = (
    items: ServiceBundleItemWithService[],
    bundleName: string,
    fromKey?: string,
  ) => {
    const unpriced: string[] = [];

    const added = items.map((item) => {
      const match = priced(item.service_id);
      // A rate set on the bundle is the package's agreed price for the service,
      // so it outranks the cascade — and lands as `custom`, so a change of
      // invoice date does not reprice it back.
      const bundled = item.fee != null;
      const source: RateSource = bundled
        ? "custom"
        : match && feeForRateSource(match, defaultSource) != null
          ? defaultSource
          : "somasi_standard";
      const rate = bundled ? item.fee : match ? feeForRateSource(match, source) : null;

      if (rate == null) unpriced.push(item.service_name ?? "a service");

      return {
        description: item.description
          ? [item.description, period].filter(Boolean).join(" — ")
          : match
            ? autoDescription(match)
            : item.service_invoice_description || item.service_name || "",
        quantity: Number(item.quantity) || 1,
        unit_amount: rate ?? 0,
        service_id: item.service_id,
        // The page's own catalogue row first, as `chooseService` uses: a line
        // whose label is blank is refused on save, and `label` is what the Charge
        // column shows.
        label: match?.name ?? item.service_name ?? "",
        source_type: "service" as const,
        source_id: item.service_id,
        rate_source: rate == null ? ("custom" as RateSource) : source,
        service_date: invoiceDate,
      };
    });

    // Only an untouched line is consumed — see `addLines`. A bundle chosen on a
    // line that already has a charge on it is the operator adding to the invoice,
    // not replacing what they typed.
    addLines(added, fromKey);

    if (unpriced.length > 0) {
      notify.warning(
        `${bundleName}: no published rate for ${unpriced.join(", ")} — ` +
          `${unpriced.length === 1 ? "that line needs" : "those lines need"} a figure`,
      );
    }
  };

  /** Fetches the bundle, then expands it onto the line it was chosen on. */
  const chooseBundle = async (key: string, bundle: PickedBundle) => {
    const items = await takeBundleItems(bundle);
    if (items) expandBundle(items, bundle.name, key);
  };

  const addAllRecoverables = () => {
    if (offered.length === 0) {
      notify.info("Everything unbilled is already on this invoice");
      return;
    }
    addPicks(offered);
  };

  const total = useMemo(() => lines.reduce((s, l) => s + l.quantity * l.unit_amount, 0), [lines]);

  /**
   * Unticking drops a note from this invoice; ticking puts it back where the
   * notes list says it goes, rather than at the end — that order is what the
   * maintenance screen's drag handles are for.
   */
  const toggleNote = (id: string) => {
    if (noteIds.includes(id)) untickedNotes.current.add(id);
    else untickedNotes.current.delete(id);
    setNoteIds(ticked(shownNotes));
  };

  /** Adds a note the rule did not bring. It arrives ticked, which is the point. */
  const addNote = (id: string) => {
    if (!id) return;
    untickedNotes.current.delete(id);
    setExtraNoteIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  /** Why a note is ticked, for the badge beside it. Null means "nothing brings it". */
  const noteReason = (note: InvoiceNote): string | null => {
    if (note.is_standard) return "Standard";
    if (companyId && (note.company_ids ?? []).includes(companyId)) return "This company";
    if (groupId && (note.client_group_ids ?? []).includes(groupId)) return "This client group";
    if (bankId && (note.bank_account_ids ?? []).includes(bankId)) return "This bank account";
    return null;
  };

  const save = async (status: "draft" | "sent") => {
    if (!companyId) return notify.error("Pick a company");
    if (lines.length === 0) return notify.error("Add at least one line");
    // A line nobody has named would print as a blank row at zero.
    if (lines.some((l) => isComposed(l) && !l.label.trim())) {
      return notify.error("Every line needs a charge — pick a service, or name it on the line");
    }
    if (lines.some((l) => !l.description.trim())) {
      return notify.error("Every line needs a description");
    }
    // What the invoice composed itself goes into the work log on save, and a
    // logged service with no date is not a record of anything.
    if (lines.some((l) => isComposed(l) && !ISO_DATE.test(l.service_date))) {
      return notify.error("Every charge needs the date the work was provided");
    }
    setSaving(true);

    try {
      // One request writes the header, its lines and the source stamps together,
      // so an interrupted save can no longer leave an invoice without lines or
      // leave billed work still marked billable.
      const invoice = await createInvoice({
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        company_id: companyId,
        // Read off the company, and null for a company in no client group.
        client_group_id: groupId || null,
        bill_to_contact_id: contactId || null,
        bank_account_id: bankId || null,
        note_ids: noteIds,
        period_label: period || null,
        // An emptied editor still holds an empty paragraph; that is no note.
        notes: richTextToPlain(freeNotes) ? freeNotes : null,
        terms: printedOverride("terms"),
        bill_to_name: printedOverride("bill_to_name"),
        bill_to_for: printedOverride("bill_to_for"),
        bill_to_address: printedOverride("bill_to_address"),
        from_name: printedOverride("from_name"),
        from_address: printedOverride("from_address"),
        status,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit_amount: l.unit_amount,
          service_id: l.service_id,
          source_type: l.source_type,
          source_id: l.source_id,
          // Only a composed line's date means anything to the writer; work drawn
          // from the log is already recorded under its own.
          service_date: isComposed(l) ? l.service_date : null,
        })),
      });

      // Archive a PDF copy in the background; failure here must not block the save.
      void requestInvoiceArchive(invoice.id).catch((error) =>
        console.warn("Could not queue the PDF archive", error),
      );

      // The save both stamped logged work as billed and logged the services this
      // invoice composed, so every view of the work log is now out of date.
      void qc.invalidateQueries({ queryKey: ["service_events"] });
      void qc.invalidateQueries({ queryKey: ["sundry_expenses"] });
      void qc.invalidateQueries({ queryKey: ["work_log_unbilled"] });
      void qc.invalidateQueries({ queryKey: ["unbilled_events"] });
      void qc.invalidateQueries({ queryKey: ["unbilled_sundry"] });
      void qc.invalidateQueries({ queryKey: ["unbilled_virrgin"] });

      notify.success(`Invoice ${invoice.invoice_number} created`);
      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
    } catch (error) {
      notify.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="text-muted-foreground">You don't have permission to create invoices.</div>
    );
  }

  /**
   * Who the invoice may be addressed to.
   *
   * The company's own contacts and its client group's — the people entitled to
   * receive this bill, and nobody else's.
   */
  const billToContacts = contacts.filter((c) => {
    if (!company) return false;
    return (
      c.company_id === company.id ||
      (!!company.client_group_id && c.client_group_id === company.client_group_id)
    );
  });

  const companyChosen = !!companyId;

  return (
    <div className="space-y-5">
      <PageHeader
        title="New invoice"
        description="Pick the client, take in what is already billable, then add the services to charge."
        breadcrumbs={[{ label: "Invoices", to: "/invoices" }, { label: "New invoice" }]}
        actions={
          <Button variant="outline" asChild>
            <Link to="/invoices">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bill to</CardTitle>
          <CardDescription>
            The company being billed. Its client group follows from the register, and decides which
            fee schedule prices the services below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label required>Company</Label>
              <CompanySelect
                value={companyId}
                onChange={(id) => {
                  // The row arrives with `onPick`; a cleared value has none.
                  if (!id) setCompany(null);
                }}
                onPick={(o) => setCompany(o.row ?? null)}
                showClientGroup={false}
              />
            </div>
            <div className="space-y-2">
              <Label>Client group</Label>
              {/* Settled by the company, so it reads rather than asks. */}
              <div className="flex h-9 items-center gap-2 rounded-md border bg-muted/40 px-3 text-sm">
                {!companyId ? (
                  <span className="text-muted-foreground">Follows the company</span>
                ) : group ? (
                  <>
                    <span className="truncate">{group.name}</span>
                    <Badge variant="outline" className="shrink-0 text-[9px] font-normal">
                      auto
                    </Badge>
                  </>
                ) : (
                  <span className="text-muted-foreground">This company is in no client group</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* The company and its group stay above the tabs: everything in both of
          them is read off that choice, so it is not something to go looking for
          behind one. The rest splits in two — what the document says, and what
          it charges for — because one screen of it was a page-long scroll. */}
      <Tabs defaultValue="charges" className="space-y-5">
        <TabsList>
          <TabsTrigger value="charges">Details &amp; lines</TabsTrigger>
          <TabsTrigger value="document">What prints &amp; notes</TabsTrigger>
        </TabsList>

        <TabsContent value="charges" className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invoice details</CardTitle>
              <CardDescription>
                The date decides which version of a fee schedule prices the lines below. Saved as a
                draft it is used for that and nothing else — a draft is dated and numbered when it
                is issued.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Invoice number</Label>
                {/* Blank, and nothing is allocated until the invoice is saved: a
                    number was once taken from the client-facing series every time
                    this page opened, which spent 424 of them behind six invoices.
                    Typing one is still honoured, for an invoice raised elsewhere
                    that is being recorded here. */}
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Assigned when saved"
                />
              </div>
              <div className="space-y-2">
                <Label>Invoice date</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => {
                    setInvoiceDate(e.target.value);
                    setDueDate(e.target.value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Kept only when you save and send. A draft is dated on the day it is issued.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Due date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Period (added to descriptions)</Label>
                <Input
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  placeholder="e.g. FY2026 or January 2026"
                />
              </div>
              <div className="space-y-2">
                <Label>Bill-to contact</Label>
                <Select value={contactId} onValueChange={setContactId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {billToContacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Receiving bank account</Label>
                <Select value={bankId} onValueChange={setBankId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.account_name} · {b.bank_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Step one of composing the invoice: what is already billable. Taking
            these in first is what stops work being logged, forgotten and never
            charged — the services to add are the deliberate part, and they come
            after. */}
          {companyChosen && (
            <Card>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">Unbilled work & recoverable expenses</CardTitle>
                  <CardDescription>
                    Everything logged for this company and not yet invoiced. Tick what belongs on
                    this invoice, or take the lot.
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={offered.length === 0}
                  onClick={addAllRecoverables}
                >
                  <Wand2 className="mr-2 h-4 w-4" /> Add all
                  {offered.length > 0 ? ` (${offered.length})` : ""}
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {allPicks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing logged for this company is waiting to be billed.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative min-w-56 flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="Search description, type or date…"
                          value={pickSearch}
                          onChange={(e) => setPickSearch(e.target.value)}
                        />
                      </div>
                      {/* The three kinds as a filter rather than three columns: one
                        list reads the same at four rows and at four hundred, and
                        the counts say where the work is without opening each. */}
                      <div className="flex flex-wrap gap-1">
                        <KindFilter
                          active={pickKind === "all"}
                          count={offered.length}
                          label="All"
                          onClick={() => setPickKind("all")}
                        />
                        {(Object.keys(PICK_KIND_LABEL) as PickKind[]).map((kind) => (
                          <KindFilter
                            key={kind}
                            active={pickKind === kind}
                            count={kindCounts[kind]}
                            label={PICK_KIND_LABEL[kind]}
                            onClick={() => setPickKind(kind)}
                          />
                        ))}
                      </div>
                    </div>

                    {selection.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                        <span className="text-sm font-medium">
                          {selection.length} selected · {money(selectionTotal)}
                        </span>
                        <div className="flex-1" />
                        <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                          Clear
                        </Button>
                        <Button size="sm" onClick={() => addPicks(selection)}>
                          <Plus className="mr-2 h-4 w-4" /> Add selected
                        </Button>
                      </div>
                    )}

                    {offered.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Everything logged is already on this invoice.
                      </p>
                    ) : filtered.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Nothing matches that search.</p>
                    ) : (
                      <Table
                        containerClassName="max-h-96 rounded-md border"
                        className="min-w-[40rem] table-fixed"
                      >
                        <TableHeader sticky>
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox
                                aria-label="Select everything shown"
                                checked={
                                  filteredPickedCount === filtered.length
                                    ? true
                                    : filteredPickedCount > 0
                                      ? "indeterminate"
                                      : false
                                }
                                onCheckedChange={() =>
                                  toggleFiltered(filteredPickedCount < filtered.length)
                                }
                              />
                            </TableHead>
                            <TableHead className="w-28">Date</TableHead>
                            <TableHead className="w-32">Kind</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="w-32 text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {shown.map((p) => (
                            <TableRow
                              key={p.key}
                              className="cursor-pointer"
                              onClick={() => togglePick(p.key)}
                            >
                              <TableCell>
                                <Checkbox
                                  aria-label={p.title}
                                  checked={picked.has(p.key)}
                                  onCheckedChange={() => togglePick(p.key)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-muted-foreground">
                                {p.date}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-normal">
                                  {PICK_KIND_LABEL[p.kind]}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Truncated text={p.title} />
                                {p.note && (
                                  <span className="block text-xs text-muted-foreground">
                                    {p.note}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-right">
                                {money(p.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {filtered.length > 0 && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          Showing {shown.length} of {filtered.length}
                          {filtered.length !== offered.length
                            ? ` (${offered.length} unbilled)`
                            : ""}
                        </span>
                        {shown.length < filtered.length && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPickLimit((n) => n + PICK_PAGE)}
                          >
                            Show {Math.min(PICK_PAGE, filtered.length - shown.length)} more
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Invoice lines</CardTitle>
                <CardDescription>
                  Pick a service or a bundle from the catalogue, or name the charge on the line
                  itself. A service offers its published rates for the invoice date; "own value"
                  takes a figure you type. A bundle fills in a line per service in it. Say when each
                  charge was provided — anything billed here that was not already in the work log is
                  written into it, marked billed, when the invoice is saved.
                </CardDescription>
              </div>
              {/* Only "Add line" here now. Bundles used to have a picker of their
                  own beside it, which made them a different gesture from choosing
                  any other charge — they are now in each line's charge picker,
                  above the services, where picking one is the same act as picking
                  a service and simply answers with several lines. */}
              <Button size="sm" variant="outline" disabled={!companyChosen} onClick={addBlankLine}>
                <Plus className="mr-2 h-4 w-4" /> Add line
              </Button>
            </CardHeader>
            <CardContent>
              {/* `table-fixed`, and a minimum width under it: the column widths are
                then the ones declared here rather than whatever the longest
                service name asks for — which is what used to stretch Charge until
                Qty and Rate were slivers. Below that minimum the box scrolls
                sideways instead of crushing the columns. A long invoice scrolls
                its rows under pinned headings rather than running the page on. */}
              <Table
                ref={linesTable}
                containerClassName="max-h-[34rem] rounded-md border"
                className="min-w-[72rem] table-fixed"
              >
                <TableHeader sticky>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead className="w-64">
                      Charge
                      <span aria-hidden="true" className="ml-0.5 text-destructive">
                        *
                      </span>
                    </TableHead>
                    <TableHead>
                      Description
                      <span aria-hidden="true" className="ml-0.5 text-destructive">
                        *
                      </span>
                    </TableHead>
                    <TableHead className="w-40">
                      Provided
                      <span aria-hidden="true" className="ml-0.5 text-destructive">
                        *
                      </span>
                    </TableHead>
                    <TableHead className="w-20 text-right">Qty</TableHead>
                    <TableHead className="w-52">Rate</TableHead>
                    <TableHead className="w-28 text-right">Amount</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                        {companyChosen
                          ? "No lines yet — take in the unbilled work above, or add a line."
                          : "Pick who the invoice is for first — the rates depend on it."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    lines.map((l, i) => {
                      const match = priced(l.service_id);
                      return (
                        <TableRow key={l.key}>
                          <TableCell className="align-top pt-4 text-muted-foreground">
                            {i + 1}
                          </TableCell>

                          <TableCell className="align-top">
                            {/* Every line is picked the same way, whether the
                              charge comes from the catalogue, from the work log,
                              or from nowhere but this line. A line drawn from the
                              log can be re-pointed at something else; what it is
                              drawn from now is said under the picker. */}
                            <ChargePicker
                              value={l.label}
                              serviceId={l.service_id}
                              sourceKey={
                                l.source_id && !isComposed(l)
                                  ? pickKey(l.source_type, l.source_id)
                                  : null
                              }
                              services={chargeables}
                              picks={picksForLine(l)}
                              onSelectService={(id) => chooseService(l.key, id)}
                              onSelectBundle={(bundle) => void chooseBundle(l.key, bundle)}
                              onSelectPick={(pick) => usePick(l.key, pick)}
                              onNameOnLine={(text) => nameOnLine(l.key, text)}
                            />
                            {!isComposed(l) && (
                              <Badge variant="outline" className="mt-1 text-[10px] font-normal">
                                {PICK_KIND_LABEL[l.source_type as PickKind]}
                              </Badge>
                            )}
                          </TableCell>

                          <TableCell className="align-top">
                            {/* A textarea rather than a single line: a charge often
                              needs a sentence of explanation under its name, and
                              the PDF prints the line breaks as typed. It grows to
                              what it holds, up to a point — a box that scrolled
                              its own text away left two rows reading as one. */}
                            <Textarea
                              rows={2}
                              className="field-sizing-content max-h-48 min-h-16 resize-y text-sm"
                              value={l.description}
                              onChange={(e) => patchLine(l.key, { description: e.target.value })}
                              placeholder="Description & notes — printed on the invoice"
                            />
                          </TableCell>

                          <TableCell className="align-top">
                            {/* When the work was done, which is what logs it.
                              Editable only on a line this invoice composed: a
                              line drawn from the work log is already recorded
                              under a date, and the place to correct that is the
                              log itself, not a copy of it here. */}
                            {isComposed(l) ? (
                              <>
                                <Input
                                  type="date"
                                  value={l.service_date}
                                  onChange={(e) =>
                                    patchLine(l.key, { service_date: e.target.value })
                                  }
                                />
                                <span className="mt-1 block text-[10px] text-muted-foreground">
                                  logged as delivered
                                </span>
                              </>
                            ) : (
                              <div className="pt-2.5">
                                <span className="text-sm">{l.service_date}</span>
                                <span className="mt-1 block text-[10px] text-muted-foreground">
                                  already logged
                                </span>
                              </div>
                            )}
                          </TableCell>

                          <TableCell className="align-top">
                            <Input
                              className="text-right"
                              type="number"
                              step="any"
                              value={l.quantity}
                              onChange={(e) =>
                                patchLine(l.key, { quantity: Number(e.target.value) || 0 })
                              }
                            />
                          </TableCell>

                          <TableCell className="align-top">
                            {l.source_type === "service" && match ? (
                              <RatePicker
                                line={l}
                                service={match}
                                groupName={group?.name ?? null}
                                onSelect={(source) => setRateSource(l.key, source)}
                                onType={(value) => patchLine(l.key, { unit_amount: value })}
                              />
                            ) : (
                              // A charge named on the line, or work drawn from the
                              // log: nothing published prices it, so the figure is
                              // simply the figure.
                              <>
                                <Input
                                  className="text-right"
                                  type="number"
                                  step="any"
                                  value={l.unit_amount}
                                  onChange={(e) =>
                                    patchLine(l.key, { unit_amount: Number(e.target.value) || 0 })
                                  }
                                />
                                <span className="mt-1 block text-[10px] text-muted-foreground">
                                  {l.source_type === "manual" ? "own value" : "as logged"}
                                </span>
                              </>
                            )}
                          </TableCell>

                          <TableCell className="align-top pt-4 text-right font-medium">
                            {money(l.quantity * l.unit_amount)}
                          </TableCell>

                          <TableCell className="align-top">
                            <Button variant="ghost" size="icon" onClick={() => removeLine(l.key)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <div className="mt-4 flex justify-end text-lg font-semibold">
                Total: {money(total)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="document" className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What the invoice prints</CardTitle>
              <CardDescription>
                The words this invoice will carry, top to bottom: the letterhead, who it is
                addressed to, and the notes under the payment instructions. They start from the
                Somasi letterhead and from the client group&apos;s address — or the company&apos;s
                registered office, for a company in no group. Editing one changes this invoice
                alone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Letterhead name</Label>
                  <Input
                    value={printingForm.from_name}
                    onChange={(e) => setPrinting({ ...printingForm, from_name: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    The phone, email and website under the address are not printed from here — they
                    are how the client replies.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Letterhead address</Label>
                  <Textarea
                    rows={4}
                    value={printingForm.from_address}
                    onChange={(e) => setPrinting({ ...printingForm, from_address: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Bill to</Label>
                  <Input
                    placeholder={companyId ? "" : "Pick a company first"}
                    value={printingForm.bill_to_name}
                    onChange={(e) => setPrinting({ ...printingForm, bill_to_name: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {!companyId
                      ? "Follows the company you pick."
                      : group
                        ? `Billed through ${group.name}.`
                        : "Billed direct to the company."}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>For</Label>
                  <Input
                    placeholder={companyId ? "" : "Pick a company first"}
                    value={printingForm.bill_to_for}
                    onChange={(e) => setPrinting({ ...printingForm, bill_to_for: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Printed under the addressee as “For: …”. A group is billed for one of its
                    companies; a company billed direct needs no such line.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Bill to address</Label>
                  <Textarea
                    rows={4}
                    value={printingForm.bill_to_address}
                    onChange={(e) =>
                      setPrinting({ ...printingForm, bill_to_address: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment terms</Label>
                  <Input
                    value={printingForm.terms}
                    onChange={(e) => setPrinting({ ...printingForm, terms: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-3 border-t pt-5">
                <div>
                  <Label>Notes on the invoice</Label>
                  <p className="text-xs text-muted-foreground">
                    Ticked notes print in the tinted panel exactly as they are written, in the order
                    the notes list is kept in. These are the standard wording plus whatever is
                    attached to this client, its group or the receiving account — untick any that
                    should not go out, or add one of the others below.
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {shownNotes.map((n) => {
                    const idx = noteIds.indexOf(n.id);
                    const reason = noteReason(n);
                    return (
                      <label
                        key={n.id}
                        className="flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm"
                      >
                        <Checkbox checked={idx >= 0} onCheckedChange={() => toggleNote(n.id)} />
                        <span className="min-w-0">
                          <span className="font-medium">
                            {idx >= 0 ? `${idx + 1}. ` : ""}
                            {n.label}
                          </span>
                          <Badge variant="outline" className="ml-2 align-middle text-[10px]">
                            {reason ?? "Added"}
                          </Badge>
                          {/* The wording is markup now; the label shows what it
                              says, and the invoice is where it is drawn. */}
                          <span className="block whitespace-pre-line text-xs text-muted-foreground">
                            {richTextToPlain(n.body)}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                  {shownNotes.length === 0 && (
                    <p className="text-sm text-muted-foreground sm:col-span-2">
                      {notes.length === 0
                        ? "No saved notes yet — add them under Invoice Notes."
                        : "No note applies to this invoice on its own. A note arrives by being standard wording, or by being attached to this company, its client group or the receiving account — add any of the others below."}
                    </p>
                  )}
                </div>
                {/* The rest of the active list — what is already on the invoice
                    is not offered again. Adding one is a decision about this
                    invoice; attaching it on the notes screen is what makes it
                    arrive by itself next time. */}
                {addableNotes.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value="" onValueChange={addNote}>
                      <SelectTrigger className="w-72">
                        <SelectValue placeholder="Add another saved note…" />
                      </SelectTrigger>
                      <SelectContent>
                        {addableNotes.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            {n.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">
                      {addableNotes.length} other{addableNotes.length === 1 ? "" : "s"} available
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Additional free-text note</Label>
                  {/* The same editor as the saved notes: it prints beside the
                      balance with whatever formatting it is given. */}
                  <RichTextEditor
                    value={freeNotes}
                    onChange={setFreeNotes}
                    minHeight="5rem"
                    aria-label="Additional free-text note"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2 pb-8">
        <Button variant="outline" disabled={saving} onClick={() => save("draft")}>
          Save as draft
        </Button>
        <Button disabled={saving} onClick={() => save("sent")}>
          {saving ? "Saving…" : "Create & mark sent"}
        </Button>
      </div>
    </div>
  );
}

/** The highlighted-item value standing for "name this charge on the line". */
const CREATE_ITEM = "__create__";

/**
 * A bundle's row value, namespaced.
 *
 * The picker's rows are keyed on the thing they stand for — a service by its id,
 * a piece of unbilled work by `pickKey` — and cmdk needs those to be distinct
 * across the whole list, not just within a group.
 */
const bundleItemValue = (id: string) => `bundle:${id}`;

/**
 * What a line charges for: a service from the catalogue, or a name given here.
 *
 * Three things can answer it, under one search box: the bundles, the unbilled work
 * waiting on this company, and the catalogue. A bundle is the odd one out — it
 * does not become this line's charge, it fills in a line per service in it — and
 * it is here anyway because "what is this line for" is where an operator asks the
 * question, and a picker of its own beside Add line made bundles a gesture you
 * had to know about.
 *
 * A combobox rather than a select, because the catalogue runs to dozens of
 * services and scrolling a list that long to find "Registered office" is worse
 * than typing three letters of it. The same box takes the one-off charge: text
 * matching no service offers itself as this line's name, so naming something the
 * catalogue has never heard of needs no second control and no mode to switch
 * into first.
 *
 * Three things here are deliberate, and each fixes a way the obvious version of
 * this control misbehaves:
 *
 *  - **It filters the list itself** (`shouldFilter={false}`). `cmdk` ranks matches
 *    by score and re-sorts on every keystroke, so rows overtake each other as you
 *    type and the thing you were reaching for moves out from under the cursor. A
 *    plain substring match keeps the catalogue's own order — on-plan first, then
 *    by name — so the list only ever gets shorter.
 *  - **It drives the highlight** (`value`/`onValueChange` on `Command`). Left to
 *    itself, `cmdk` highlights the first row, which is the Create row — so Enter
 *    named a one-off charge even when the search had found the exact service. The
 *    highlight is pointed at the best match instead, and only falls to Create
 *    when nothing matches at all. Arrow keys still move it freely.
 *  - **The trigger is one line high**, truncating rather than wrapping. A trigger
 *    that grows to fit a long service name changes the table row's height when a
 *    service is picked, which jumps everything below it.
 */
function ChargePicker({
  value,
  serviceId,
  sourceKey,
  services,
  picks,
  onSelectService,
  onSelectBundle,
  onSelectPick,
  onNameOnLine,
}: {
  /** The charge's current name, whichever kind it is. */
  value: string;
  /** Set when the name belongs to a catalogue service. */
  serviceId: string | null;
  /** Set when the line is drawn from the work log, so its row reads as chosen. */
  sourceKey: string | null;
  services: Chargeable[];
  /** Unbilled work still free to be put on a line. */
  picks: Pick[];
  onSelectService: (serviceId: string) => void;
  /** A bundle answers with several lines; this one is one of them or is dropped. */
  onSelectBundle: (bundle: PickedBundle) => void;
  onSelectPick: (pick: Pick) => void;
  onNameOnLine: (label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlight, setHighlight] = useState<string>(CREATE_ITEM);

  const typed = search.trim();

  /**
   * The bundles matching what is typed, read from the API rather than held.
   *
   * The catalogue above is on the page already, for the rates; bundles are not,
   * and downloading the table to feed a dropdown is the thing every picker here
   * was changed to stop doing — a bundle list is short today, but so was the
   * service list once. Only while the popover is open, and only on the settled
   * text, so arrowing through the list is not a request per keystroke.
   */
  const bundleQuery = useDebounced(typed);
  const { data: bundles = [] } = useQuery({
    queryKey: ["service_bundles_charge_picker", bundleQuery],
    enabled: open,
    queryFn: () => searchServiceBundles(bundleQuery),
  });

  // Substring on the name or the code, in the order the catalogue was given in.
  const matches = useMemo(() => {
    const needle = typed.toLowerCase();
    if (!needle) return services;
    return services.filter(
      (s) => s.name.toLowerCase().includes(needle) || (s.code ?? "").toLowerCase().includes(needle),
    );
  }, [services, typed]);

  /**
   * The unbilled work this line could be pointed at, narrowed by the same text.
   *
   * Capped rather than listed in full: a company with hundreds logged would
   * otherwise render all of them into every line's popover, and the search box
   * above is the faster way through them anyway.
   */
  const pickMatches = useMemo(() => {
    const needle = typed.toLowerCase();
    if (!needle) return picks;
    return picks.filter((p) => pickHaystack(p).includes(needle));
  }, [picks, typed]);
  const shownPicks = pickMatches.slice(0, PICK_PAGE);

  // Text that already names a service exactly is not a new charge — selecting
  // that service is the better answer, so Create stays out of the way.
  const namesAService = services.some((s) => s.name.toLowerCase() === typed.toLowerCase());
  const offerCreate = !!typed && !namesAService;

  const bestMatch = matches[0]?.service_id ?? null;
  const bestBundle = bundles[0] ? bundleItemValue(bundles[0].id) : null;

  // Point the highlight at the best match as the search narrows, so Enter takes
  // the charge the operator is plainly aiming at. Keyed on the best match
  // rather than on every render, which is what leaves the arrow keys working.
  //
  // A service first, then a bundle: typing the name of a bundle that matches no
  // service would otherwise leave Enter on "Create …", which invents a one-off
  // charge at zero in place of the bundle sitting right there on the list.
  useEffect(() => {
    setHighlight(bestMatch ?? bestBundle ?? CREATE_ITEM);
  }, [bestMatch, bestBundle, typed]);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between text-left text-sm font-normal"
        >
          <Truncated
            text={value || "Select or create a charge"}
            className={cn("min-w-0 text-left", !value && "text-muted-foreground")}
          />
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] p-0" align="start">
        <Command shouldFilter={false} value={highlight} onValueChange={setHighlight}>
          <CommandInput
            placeholder="Search services, or type a new name"
            value={search}
            onValueChange={setSearch}
          />
          {/* A floor as well as a ceiling: a list that shrinks from forty rows
              to two resizes the popover under the cursor, and Radix re-anchors
              it when it no longer fits below the trigger — which reads as the
              whole thing jumping as you type. */}
          <CommandList className="max-h-72 min-h-[12rem]">
            {offerCreate && (
              <>
                <CommandGroup heading="New charge">
                  <CommandItem
                    value={CREATE_ITEM}
                    onSelect={() => {
                      onNameOnLine(typed);
                      close();
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">Create “{typed}”</span>
                      <span className="block text-xs text-muted-foreground">
                        Used on this line only — not added to the catalogue
                      </span>
                    </span>
                  </CommandItem>
                </CommandGroup>
                {/* `alwaysRender`: cmdk hides a separator whenever the search
                    box has text, which is precisely when this one is shown. */}
                <CommandSeparator alwaysRender />
              </>
            )}

            {shownPicks.length > 0 && (
              <>
                <CommandGroup heading={`Unbilled work (${pickMatches.length})`}>
                  {shownPicks.map((p) => (
                    <CommandItem
                      key={p.key}
                      value={p.key}
                      onSelect={() => {
                        onSelectPick(p);
                        close();
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          sourceKey === p.key ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <Truncated text={p.title} side="right" />
                        <span className="block text-xs text-muted-foreground">
                          {PICK_KIND_LABEL[p.kind]} · {p.date} · {money(p.amount)}
                        </span>
                        {/* A bundled entry takes this line's place with several
                            of its own, so the row says so before it is chosen. */}
                        {p.lines.length > 1 && (
                          <span className="block text-xs text-muted-foreground">
                            {p.note ?? `itemises into ${p.lines.length} lines`}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  ))}
                  {pickMatches.length > shownPicks.length && (
                    <p className="px-2 py-1.5 text-xs text-muted-foreground">
                      {pickMatches.length - shownPicks.length} more — type to narrow
                    </p>
                  )}
                </CommandGroup>
                <CommandSeparator alwaysRender />
              </>
            )}

            {bundles.length > 0 && (
              <>
                {/* Above the services, not among them: a bundle is not one charge
                    and choosing it does not leave the line naming it — it fills
                    in a line per service, which is said on the row itself so the
                    expansion is not a surprise. */}
                <CommandGroup heading={typed ? `Bundles (${bundles.length})` : "Bundles"}>
                  {bundles.map((bundle) => (
                    <CommandItem
                      key={bundle.id}
                      value={bundleItemValue(bundle.id)}
                      onSelect={() => {
                        onSelectBundle({ id: bundle.id, name: bundle.name });
                        close();
                      }}
                    >
                      <Boxes className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <Truncated text={bundle.name} side="right" />
                        <span className="block text-xs text-muted-foreground">
                          {bundle.code ? `${bundle.code} · ` : ""}adds a line per service
                        </span>
                      </span>
                    </CommandItem>
                  ))}
                  {bundles.length >= PICKER_LIMIT && (
                    <p className="px-2 py-1.5 text-xs text-muted-foreground">
                      More bundles than fit — type to narrow
                    </p>
                  )}
                </CommandGroup>
                <CommandSeparator alwaysRender />
              </>
            )}

            {matches.length > 0 ? (
              <CommandGroup heading={typed ? `Services (${matches.length})` : "Services"}>
                {matches.map((s) => (
                  <CommandItem
                    key={s.service_id}
                    value={s.service_id}
                    onSelect={() => {
                      onSelectService(s.service_id);
                      close();
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        serviceId === s.service_id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <Truncated text={s.name} side="right" className="min-w-0 flex-1" />
                    {s.in_plan && (
                      <Badge variant="outline" className="ml-2 shrink-0 text-[9px] font-normal">
                        on plan
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              // Not `CommandEmpty`: that counts the rows cmdk itself filtered,
              // and this list does its own filtering, so it would never show.
              <p className="px-3 py-4 text-sm text-muted-foreground">
                {services.length === 0
                  ? "No services in the catalogue."
                  : typed
                    ? "No service matches that name."
                    : "No services available."}
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The rate a service line bills, chosen from what is published for it.
 *
 * The two schedule options carry their figures in the option itself, so picking
 * a level and seeing what it costs is one glance rather than a choice followed by
 * a surprise. Both are read as at the invoice date, so the number shown is the
 * one on the version in force then, not the schedule's newest.
 *
 * "Own value" is what lets a charge go out at a figure no schedule publishes, and
 * it is a choice rather than an unannounced edit: the figure is then the
 * operator's and survives a change of invoice date, where the other two re-read
 * themselves.
 */
function RatePicker({
  line,
  service,
  groupName,
  onSelect,
  onType,
}: {
  line: Line;
  service: Chargeable;
  /** The client group's own name, so its option reads "Astride Group" rather than a level. */
  groupName: string | null;
  onSelect: (source: RateSource) => void;
  onType: (value: number) => void;
}) {
  const sourceLabel = (source: RateSource) =>
    source === "client_group" && groupName ? groupName : RATE_SOURCE_LABEL[source];

  const effectiveFrom =
    line.rate_source === "client_group"
      ? service.group_effective_from
      : line.rate_source === "somasi_standard"
        ? service.standard_effective_from
        : null;

  return (
    <div className="space-y-1">
      <Select value={line.rate_source} onValueChange={(v) => onSelect(v as RateSource)}>
        <SelectTrigger className="h-8 text-xs">
          {/* The level only. Each option carries its figure, because choosing
              between them means comparing them — but once chosen the figure is
              on its own line below, where a long one is not truncated. */}
          <SelectValue>{sourceLabel(line.rate_source)}</SelectValue>
        </SelectTrigger>
        {/* Wider than the trigger, which is only as wide as the Rate column: the
            options name a schedule *and* its figure, and both have to be read
            whole to be compared. */}
        <SelectContent align="end" className="w-80">
          {RATE_SOURCES.map((source) => {
            const rate = feeForRateSource(service, source);
            const unpriced = source === "client_group" && service.group_fee == null;
            return (
              <SelectItem
                key={source}
                value={source}
                // A group that prices nothing for this service has no figure to
                // offer, so the option would resolve to no rate at all.
                disabled={unpriced}
              >
                <span className="flex items-baseline justify-between gap-4">
                  <span className="min-w-0 truncate">{sourceLabel(source)}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {rate != null ? money(rate) : unpriced ? "not priced" : ""}
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {line.rate_source === "custom" ? (
        <Input
          className="h-8 text-right text-sm"
          type="number"
          step="any"
          value={line.unit_amount}
          onChange={(e) => onType(Number(e.target.value) || 0)}
        />
      ) : (
        <div className="px-1 text-right text-sm font-medium">{money(line.unit_amount)}</div>
      )}

      {/* Which dated version answered — the one in force on the invoice date,
          not the schedule's newest. */}
      <span className="block text-[10px] text-muted-foreground">
        {line.rate_source === "custom"
          ? "typed in for this line"
          : effectiveFrom
            ? `rates of ${effectiveFrom}`
            : "standard fee — no dated version"}
      </span>
    </div>
  );
}

/**
 * How a line drawn from the work log reads on the invoice.
 *
 * No company name is prefixed: the invoice is raised to one company, so naming
 * it again on every line only repeats the addressee.
 */
function eventDescription(event: { description: string | null }): string {
  return event.description ?? "Service delivered";
}

/**
 * A logged service as invoice lines — one line, or several for a bundle.
 *
 * This is where a bundle logged as a single entry is itemised for the client. The
 * work log holds one row, because one package was delivered and one price was
 * agreed for it; the invoice lists the services, because that is what the client
 * is reading. The breakdown stored on the row is what it is split by, so an
 * invoice itemises the bundle as it stood when the work was done rather than as
 * the catalogue has it today.
 *
 * Every line names the same logged row as its source. That is what keeps the
 * entry a single piece of work: it is offered once in the picker, billed once,
 * and stamped once — and taking the last of its lines off the invoice puts the
 * whole entry back on offer.
 *
 * The lines carry the figures the entry was logged at, as every other piece of
 * drawn-in work does — `rate_source` is `custom`, so a change of invoice date
 * does not reprice work that was already priced when it was recorded.
 */
function eventLines(event: LoggedServiceEvent, period: string): Omit<Line, "key">[] {
  const quantity = Number(event.quantity ?? 1);
  const label = event.description ?? "Service delivered";

  const whole = (): Omit<Line, "key">[] => [
    {
      description: eventDescription(event),
      quantity,
      unit_amount: Number(event.amount ?? 0),
      service_id: event.service_id,
      label,
      source_type: "service_event",
      source_id: event.id,
      rate_source: "custom",
      service_date: event.event_date,
    },
  ];

  // `loggedServiceLines` decides what a logged entry covers and applies its
  // quantity — `amount` being the price of one bundle and `quantity` how many
  // were delivered, so the figure is split once and each line multiplied up.
  // Shared with the work-log export, which itemises a package for the same
  // reason this does; two copies of it would eventually be two answers to "what
  // did that package cover".
  const lines = loggedServiceLines(event);

  // One line that was not itemised is an entry with nothing to split: ordinary
  // work, a bundle already spread one row per service, or a package whose
  // breakdown carries no fee to apportion the price against.
  if (lines.length === 1 && !lines[0]!.itemised) return whole();

  return lines.map((line) => {
    const name = line.service_name ?? "Service";
    return {
      // The bundle's own invoice wording where it set one. Otherwise its name
      // stays in front of each service, so a client reading five lines can see
      // they are one package rather than five purchases.
      description: [line.description ?? `${event.bundle_name ?? label} — ${name}`, period]
        .filter(Boolean)
        .join(" — "),
      // Already multiplied by how many bundles were delivered.
      quantity: line.quantity,
      unit_amount: line.unit_amount ?? 0,
      service_id: line.service_id,
      label: name,
      source_type: "service_event",
      source_id: event.id,
      rate_source: "custom",
      service_date: event.event_date,
    };
  });
}

/**
 * What a logged entry says about the bundle behind it, for the picker's second
 * line. Null for work that came from no bundle, which is most of it.
 */
function bundleNote(event: LoggedServiceEvent): string | null {
  const kind = serviceEventBundleKind(event);
  if (!kind) return null;

  const name = event.bundle_name ?? "a bundle";
  if (kind === "spread") return `From bundle “${name}”`;

  const count = event.bundle_items?.length ?? 0;
  return count > 0
    ? `Bundle “${name}” — itemises into ${count} line${count === 1 ? "" : "s"}`
    : `Bundle “${name}”`;
}

function expenseDescription(expense: { description: string; expense_date: string }): string {
  return `Disbursement — ${expense.description} (${expense.expense_date})`;
}

/**
 * What a recovered registry fee is called on the invoice.
 *
 * The description is the one the statement import stored — `FSC Charges` for
 * every ordinary fee line, since the statement's own Description column holds
 * the IBC number rather than a description, and whatever the statement did print
 * for the lines that printed words. The date is the date the registry took the
 * money, which is what makes two otherwise identical lines tell themselves apart
 * on a client's invoice.
 */
function virrginDescription(txn: { description: string | null; txn_date: string }): string {
  return `${txn.description?.trim() || FSC_CHARGE_DESCRIPTION} (${txn.txn_date})`;
}

/** One kind of logged work, as a filter chip with its count. */
function KindFilter({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "secondary" : "ghost"}
      className="h-9"
      disabled={count === 0 && !active}
      onClick={onClick}
    >
      {label}
      <span className="ml-1.5 text-xs text-muted-foreground">{count}</span>
    </Button>
  );
}
